import React, { useEffect, useRef, useState } from "react";
import {
  Brain,
  GraduationCap,
  Trophy,
  BookOpen,
  CheckCircle2,
  ArrowRight,
  Monitor,
  WifiOff,
  KeyRound,
  School,
  ChevronDown,
  Apple,
  PlayCircle,
  Sparkles,
  ShieldCheck,
  Menu,
  X,
  Zap,
  Lock,
  Loader2,
  AlertCircle,
  PencilLine,
  LineChart,
  BadgeCheck,
  Layers,
} from "lucide-react";
import Hero3D from "./Hero3D";
import {
  Cloud,
  StarBurst,
  SparkleDoodle,
  Blob,
  CapBuddy,
  NigeriaMap,
} from "./graphics";

// Error boundary — catches WebGL failures from Hero3D so the rest of the
// page renders normally instead of going blank.
class WebGLErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) { console.warn("Hero3D WebGL unavailable:", err.message); }
  render() { return this.state.failed ? null : this.props.children; }
}

const APPS = [
  {
    name: "JAMB CBT",
    icon: Brain,
    description:
      "Real UTME simulation with an AI tutor, topic-by-topic practice & past questions back to 2000.",
    android: "https://play.google.com/store/apps/details?id=com.kinteron.jamb",
    ios: null,
  },
  {
    name: "WAEC CBT",
    icon: GraduationCap,
    description:
      "WAEC Objective & Theory past questions by year, every answer explained.",
    android: "https://play.google.com/store/apps/details?id=com.kinteron.waec",
    ios: null,
  },
  {
    name: "NECO CBT",
    icon: GraduationCap,
    description: "10+ years of NECO questions with analytics.",
    android: "https://play.google.com/store/apps/details?id=com.kinteron.neco",
    ios: null,
  },
  {
    name: "POST-UTME CBT",
    icon: Trophy,
    description: "University-specific screening past questions.",
    android:
      "https://play.google.com/store/apps/details?id=com.kinteron.postutme",
    ios: null,
  },
  {
    name: "KinterNotes",
    icon: BookOpen,
    description: "Free revision notes mapped to the NERDC curriculum.",
    android:
      "https://play.google.com/store/apps/details?id=com.kinteron.studynote",
    ios: null,
  },
];

const FEATURE_CARDS = [
  {
    icon: ShieldCheck,
    title: "AI tutor on every question",
    body: "Not just marked — the built-in AI tutor verifies each answer and teaches you the reasoning behind it.",
  },
  {
    icon: PencilLine,
    title: "Topic-by-topic practice",
    body: "Drill single topics until they click, then take full subject simulations that mirror the real exam.",
  },
  {
    icon: LineChart,
    title: "Revisit your past exams",
    body: "Your full exam history is saved — replay any attempt question by question and track your analytics.",
  },
  {
    icon: Layers,
    title: "Flashcards for quick revision",
    body: "New: bite-size flashcards sourced from real past questions — perfect for daily study streaks and last-minute revision.",
  },
];

const APP_MOCKUPS = [
  {
    type: "quiz",
    subject: "Chemistry",
    question: "Which gas turns lime water milky?",
    options: ["Oxygen", "Carbon (IV) oxide", "Nitrogen", "Hydrogen"],
    correct: 1,
  },
  {
    type: "quiz",
    subject: "Mathematics",
    question: "If 3x + 5 = 20, find x",
    options: ["x = 3", "x = 5", "x = 8", "x = 15"],
    correct: 1,
  },
  {
    type: "quiz",
    subject: "Government",
    question: "Nigeria's capital city is —",
    options: ["Lagos", "Kano", "Abuja", "Enugu"],
    correct: 2,
  },
  {
    type: "quiz",
    subject: "Reasoning",
    question: "Pick the shape that doesn't belong",
    options: ["Circle", "Square", "Sphere", "Triangle"],
    correct: 2,
  },
  {
    type: "notes",
    subject: "Biology",
    title: "Photosynthesis",
    lines: [
      "Plants convert light energy",
      "into chemical energy, stored",
      "as glucose in chloroplasts.",
    ],
    highlight: "chloroplasts",
  },
];

/* ------------------------------------------------------------------ */
/*  Tokens                                                              */
/* ------------------------------------------------------------------ */

const INK = "#181611";
const CREAM = "#FDF8F0";
const CREAM_CARD = "#FFF4DE";
const MINT = "#DCEEE3";
const MINT_DEEP = "#BFDFCE";
const PEACH_1 = "#F8CE85";
const PEACH_2 = "#F4AE59";
const ORANGE = "#EE7C3B";
const ORANGE_DEEP = "#D9642A";
const INDIGO = "#3D4EAD";
const INDIGO_DEEP = "#2E3B8A";
const FOREST = "#2F6B4F";
const MUTED = "#5B5748";
const LINE = "#EADFC4";
const PREMIUM_1 = "#9333EA";
const PREMIUM_2 = "#EC4899";

/* ------------------------------------------------------------------ */
/*  Utilities                                                           */
/* ------------------------------------------------------------------ */

function useScrollReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.12 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0, style = {} }) {
  const [ref, visible] = useScrollReveal();
  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(16px)",
        transition: `opacity 0.6s cubic-bezier(.2,.7,.3,1) ${delay}ms, transform 0.6s cubic-bezier(.2,.7,.3,1) ${delay}ms`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* Animated count-up number that starts when scrolled into view */
function CountUp({ to, suffix = "", duration = 1600 }) {
  const [ref, visible] = useScrollReveal();
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!visible) return undefined;
    let raf;
    const start = performance.now();
    function step(now) {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(to * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    }
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [visible, to, duration]);

  return (
    <span ref={ref}>
      {value.toLocaleString()}
      {suffix}
    </span>
  );
}

/* Infinite scrolling marquee strip */
function Marquee({ items }) {
  const row = [...items, ...items];
  return (
    <div
      style={{
        background: INK,
        padding: "18px 0",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div style={{ display: "flex", width: "max-content", animation: "marquee 28s linear infinite" }}>
        {row.map((item, i) => (
          <span
            key={`${item}-${i}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "22px",
              padding: "0 22px",
              fontFamily: "'Baloo 2', sans-serif",
              fontWeight: 700,
              fontSize: "17px",
              color: CREAM,
              whiteSpace: "nowrap",
            }}
          >
            {item}
            <StarBurst size={16} color={ORANGE} style={{ position: "relative" }} />
          </span>
        ))}
      </div>
    </div>
  );
}

/* Squiggly hand-drawn underline, used as an accent under key words */
function Squiggle({ color = ORANGE, width = 140 }) {
  return (
    <svg
      width={width}
      height="14"
      viewBox="0 0 140 14"
      fill="none"
      style={{ display: "block", marginTop: "-4px" }}
    >
      <path
        d="M2 8.5C14 2.5 22 2.5 34 8.5C46 14.5 54 14.5 66 8.5C78 2.5 86 2.5 98 8.5C110 14.5 118 14.5 130 8.5"
        stroke={color}
        strokeWidth="3.2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/* Highlight pill behind a headline word — the reference's signature move */
function HighlightWord({
  children,
  bg = ORANGE,
  color = "#FFFFFF",
  rotate = "-1.2deg",
}) {
  return (
    <span
      style={{
        display: "inline-block",
        background: bg,
        color,
        padding: "2px 18px 8px",
        borderRadius: "40% 45% 42% 48% / 55% 48% 52% 45%",
        transform: `rotate(${rotate})`,
        position: "relative",
      }}
    >
      {children}
    </span>
  );
}

function Pill({ children, tone = "cream" }) {
  const map = {
    cream: { bg: CREAM_CARD, border: LINE, color: MUTED },
    mint: { bg: "#EAF6EF", border: MINT_DEEP, color: FOREST },
    ink: { bg: INK, border: INK, color: CREAM },
  };
  const s = map[tone];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontFamily: "'Space Grotesk', sans-serif",
        fontSize: "12px",
        fontWeight: 600,
        letterSpacing: "0.01em",
        color: s.color,
        background: s.bg,
        border: `1.5px solid ${s.border}`,
        borderRadius: "999px",
        padding: "6px 16px",
      }}
    >
      {children}
    </span>
  );
}

/* Corner "quote mark" accent used on feature cards, echoing the reference */
function CornerMark({ color = ORANGE }) {
  return (
    <svg
      width="30"
      height="24"
      viewBox="0 0 30 24"
      fill="none"
      style={{ position: "absolute", top: "18px", right: "20px" }}
    >
      <path
        d="M0 24C0 10 6 1 16 0L17 6C10 7 7 11 7 17H14V24H0Z"
        fill={color}
        opacity="0.9"
      />
      <path
        d="M16 24C16 10 22 1 30 0L31 6C24 7 23 11 23 17H30V24H16Z"
        fill={color}
        opacity="0.9"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Nav — apps mega-menu + dedicated desktop tab                       */
/* ------------------------------------------------------------------ */

function AppsMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "5px",
          fontFamily: "'Space Grotesk', sans-serif",
          fontWeight: 600,
          fontSize: "14.5px",
          color: INK,
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: "8px 6px",
        }}
      >
        Apps
        <ChevronDown
          size={15}
          strokeWidth={2.4}
          style={{
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.2s ease",
          }}
        />
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 14px)",
            left: "50%",
            transform: "translateX(-50%)",
            width: "380px",
            maxWidth: "calc(100vw - 32px)",
            background: "#FFFFFF",
            borderRadius: "18px",
            boxShadow:
              "0 20px 44px rgba(24,22,17,0.16), 0 6px 16px rgba(24,22,17,0.08)",
            padding: "10px",
            zIndex: 200,
          }}
        >
          {APPS.map((app) => {
            const Icon = app.icon;
            return (
              <div
                key={app.name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "10px",
                  borderRadius: "12px",
                }}
                className="apps-menu-row"
              >
                <div
                  style={{
                    width: "38px",
                    height: "38px",
                    borderRadius: "10px",
                    background: MINT,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon size={18} color={FOREST} strokeWidth={1.8} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontFamily: "'Baloo 2', sans-serif",
                      fontWeight: 600,
                      fontSize: "14.5px",
                      color: INK,
                    }}
                  >
                    {app.name}
                  </div>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: "11.5px",
                      color: MUTED,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {app.description}
                  </div>
                </div>
                <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                  <a
                    href={app.android}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Get it on Google Play"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "30px",
                      height: "30px",
                      borderRadius: "50%",
                      background: MINT,
                      color: FOREST,
                    }}
                  >
                    <PlayCircle size={15} strokeWidth={1.9} />
                  </a>
                  <span
                    title="iOS — coming soon"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "30px",
                      height: "30px",
                      borderRadius: "50%",
                      background: "#F1EEE6",
                      color: "#B3AC98",
                    }}
                  >
                    <Apple size={15} strokeWidth={1.9} />
                  </span>
                </div>
              </div>
            );
          })}
          <div
            style={{
              marginTop: "6px",
              padding: "9px 12px",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "11px",
              color: MUTED,
              borderTop: `1px solid ${LINE}`,
            }}
          >
            <Apple
              size={11}
              style={{ verticalAlign: "-2px", marginRight: "5px" }}
            />
            iOS apps — coming soon for all five.
          </div>
        </div>
      )}

      <style>{`.apps-menu-row:hover { background: ${CREAM_CARD}; }`}</style>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  App store link pair (used elsewhere, e.g. popular apps + footer)  */
/* ------------------------------------------------------------------ */

function StoreLinks({ android, size = "md" }) {
  const h = size === "sm" ? "34px" : "40px";
  const fs = size === "sm" ? "10.5px" : "11.5px";
  return (
    <div style={{ display: "flex", gap: "8px" }}>
      <a
        href={android}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          height: h,
          padding: "0 12px",
          borderRadius: "9px",
          background: INK,
          color: CREAM,
          textDecoration: "none",
        }}
      >
        <PlayCircle size={size === "sm" ? 13 : 15} />
        <span
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 600,
            fontSize: fs,
          }}
        >
          Google Play
        </span>
      </a>
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          height: h,
          padding: "0 12px",
          borderRadius: "9px",
          background: "#EDE8DA",
          color: "#8C876F",
        }}
      >
        <Apple size={size === "sm" ? 13 : 15} />
        <span
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 600,
            fontSize: fs,
          }}
        >
          Coming soon
        </span>
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Unlock Premium modal — pays via Paystack, unlocks all 4 mobile apps */
/* ------------------------------------------------------------------ */

// Fill these in with your actual central Supabase project + anon key
const CENTRAL_SUPABASE_URL = "https://jzwpixujezkbkqjrxrhm.supabase.co";
const CENTRAL_ANON_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imp6d3BpeHVqZXprYmtxanJ4cmhtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODI2NDExODMsImV4cCI6MjA5ODIxNzE4M30.X9tDrY5ehAIfZo98FpdzKP_r0GW9zK4HrZaZaagSS14";

function UnlockPremiumModal({ open, onClose }) {
  const [email, setEmail] = useState("");
  useEffect(() => {
    if (!open) return;
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get("email");
    if (emailParam) setEmail(emailParam);
  }, [open]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  async function startCheckout() {
    const trimmed = email.trim();
    setError("");

    if (!trimmed || !trimmed.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(
        `${CENTRAL_SUPABASE_URL}/functions/v1/kinteron-payments`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${CENTRAL_ANON_KEY}`,
            apikey: CENTRAL_ANON_KEY,
          },
          body: JSON.stringify({ email: trimmed }),
        },
      );

      const data = await res.json();

      if (!res.ok || !data.authorization_url) {
        setError(data.error || "Something went wrong. Please try again.");
        setLoading(false);
        return;
      }

      window.location.href = data.authorization_url;
    } catch {
      setError("Network error. Check your connection and try again.");
      setLoading(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(24,22,17,0.55)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "420px",
          background: "#FFFFFF",
          borderRadius: "26px",
          overflow: "hidden",
          boxShadow: "0 30px 60px rgba(0,0,0,0.3)",
          position: "relative",
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute",
            top: "16px",
            right: "16px",
            width: "34px",
            height: "34px",
            borderRadius: "50%",
            border: "none",
            background: "rgba(255,255,255,0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            zIndex: 2,
          }}
        >
          <X size={18} color={INK} />
        </button>

        <div
          style={{
            background: `linear-gradient(135deg, ${PREMIUM_1}, ${PREMIUM_2})`,
            padding: "34px 28px 28px",
            color: "#FFFFFF",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "14px",
              background: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "16px",
            }}
          >
            <Zap size={24} color="#FFFFFF" strokeWidth={1.8} fill="#FFFFFF" />
          </div>
          <div
            style={{
              fontFamily: "'Baloo 2', sans-serif",
              fontWeight: 700,
              fontSize: "24px",
              marginBottom: "8px",
              lineHeight: 1.15,
            }}
          >
            Unlock Kinteron Premium
          </div>
          <p
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "14px",
              lineHeight: 1.55,
              opacity: 0.95,
              margin: 0,
            }}
          >
            ₦2,000 unlocks all 4 apps — JAMB, WAEC, NECO &amp; Post-UTME.
            One-time payment, forever access.
          </p>
        </div>

        <div style={{ padding: "26px 28px 28px" }}>
          <label
            htmlFor="unlock-email"
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "12px",
              fontWeight: 700,
              color: INK,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: "8px",
              display: "block",
            }}
          >
            Your account email
          </label>
          <input
            id="unlock-email"
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && startCheckout()}
            style={{
              width: "100%",
              padding: "14px 16px",
              fontSize: "16px",
              borderRadius: "12px",
              border: `1.5px solid ${error ? "#EF4444" : LINE}`,
              marginBottom: "6px",
              outline: "none",
              fontFamily: "'Space Grotesk', sans-serif",
            }}
          />
          <p
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "12px",
              color: MUTED,
              margin: "0 0 18px",
            }}
          >
            Use the same email you signed up with in the app — this is how we
            match your payment. {"  "} (Do not edit if already prefilled)
          </p>

          {error && (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "7px",
                marginBottom: "14px",
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "13px",
                color: "#EF4444",
              }}
            >
              <AlertCircle
                size={15}
                style={{ flexShrink: 0, marginTop: "1px" }}
              />
              <span>{error}</span>
            </div>
          )}

          <button
            onClick={startCheckout}
            disabled={loading}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              padding: "16px",
              fontSize: "15.5px",
              fontWeight: 700,
              fontFamily: "'Space Grotesk', sans-serif",
              color: "#FFFFFF",
              background: `linear-gradient(135deg, ${PREMIUM_1}, ${PREMIUM_2})`,
              border: "none",
              borderRadius: "12px",
              cursor: loading ? "default" : "pointer",
              opacity: loading ? 0.75 : 1,
            }}
          >
            {loading ? (
              <>
                <Loader2
                  size={17}
                  className="spin-slow"
                  style={{ animationDuration: "0.9s" }}
                />
                Redirecting…
              </>
            ) : (
              <>
                <Lock size={15} />
                Pay ₦2,000 with Paystack
              </>
            )}
          </button>

          <p
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "12px",
              color: MUTED,
              textAlign: "center",
              marginTop: "16px",
            }}
          >
            Already paid? Go back to the app and tap "I already paid — check
            now".
          </p>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Payment Success modal — shown when Paystack redirects the browser  */
/*  back via callback_url. Does NOT claim access is unlocked — that's  */
/*  decided server-side by the paystack-webhook, this is UI only.      */
/* ------------------------------------------------------------------ */

function PaymentSuccessModal({ open, email, onClose }) {
  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(24,22,17,0.55)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "420px",
          background: "#FFFFFF",
          borderRadius: "26px",
          padding: "36px 30px 30px",
          boxShadow: "0 30px 60px rgba(0,0,0,0.3)",
          position: "relative",
          textAlign: "center",
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute",
            top: "16px",
            right: "16px",
            width: "34px",
            height: "34px",
            borderRadius: "50%",
            border: "none",
            background: "#F5F1E6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <X size={18} color={INK} />
        </button>

        <div
          style={{
            width: "56px",
            height: "56px",
            borderRadius: "50%",
            background: MINT,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 18px",
          }}
        >
          <CheckCircle2 size={28} color={FOREST} strokeWidth={2} />
        </div>

        <div
          style={{
            fontFamily: "'Baloo 2', sans-serif",
            fontWeight: 700,
            fontSize: "22px",
            color: INK,
            marginBottom: "10px",
          }}
        >
          Payment received
        </div>

        <p
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "14px",
            color: MUTED,
            lineHeight: 1.6,
            margin: "0 0 20px",
          }}
        >
          {email
            ? `We got your payment for ${email}. `
            : "We got your payment. "}
          It can take a minute or two to reflect. Open the app and tap "I
          already paid — check now" on the unlock screen to confirm access.
        </p>

        <button
          onClick={onClose}
          style={{
            width: "100%",
            padding: "14px",
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 700,
            fontSize: "14.5px",
            color: "#FFFFFF",
            background: INK,
            border: "none",
            borderRadius: "12px",
            cursor: "pointer",
          }}
        >
          Got it
        </button>
      </div>
    </div>
  );
}
function PhoneMockup({ mock, accent, badgeBg }) {
  return (
    <div
      style={{
        width: "132px",
        height: "224px",
        background: "#FFFFFF",
        borderRadius: "22px",
        border: `3px solid ${INK}`,
        padding: "12px 10px 10px",
        boxShadow: "0 10px 22px rgba(24,22,17,0.16)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          width: "28px",
          height: "4px",
          borderRadius: "999px",
          background: "#E4DFCC",
          margin: "0 auto 10px",
        }}
      />
      <div
        style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: "7.5px",
          fontWeight: 600,
          color: accent,
          letterSpacing: "0.03em",
          marginBottom: "6px",
        }}
      >
        {mock.subject.toUpperCase()}
      </div>

      {mock.type === "quiz" ? (
        <>
          <p
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "8.5px",
              fontWeight: 600,
              color: INK,
              lineHeight: 1.35,
              marginBottom: "8px",
            }}
          >
            {mock.question}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {mock.options.map((opt, idx) => {
              const isCorrect = idx === mock.correct;
              return (
                <div
                  key={opt}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "4px 6px",
                    borderRadius: "6px",
                    background: isCorrect ? badgeBg : "#FAF8F2",
                    border: `1px solid ${isCorrect ? accent : "#EDE8D8"}`,
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: "6.5px",
                      fontWeight: 700,
                      color: isCorrect ? accent : "#B3AC98",
                    }}
                  >
                    {String.fromCharCode(65 + idx)}
                  </span>
                  <span
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: "7px",
                      color: isCorrect ? INK : "#8C876F",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {opt}
                  </span>
                  {isCorrect && (
                    <CheckCircle2
                      size={8}
                      color={accent}
                      strokeWidth={2.6}
                      style={{ marginLeft: "auto", flexShrink: 0 }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <>
          <div
            style={{
              fontFamily: "'Baloo 2', sans-serif",
              fontSize: "10px",
              fontWeight: 700,
              color: INK,
              marginBottom: "8px",
            }}
          >
            {mock.title}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            {mock.lines.map((line, i) => {
              const [before, after] = line.includes(mock.highlight)
                ? line.split(mock.highlight)
                : [line, null];
              return (
                <div
                  key={i}
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: "7.5px",
                    color: "#5B5748",
                    lineHeight: 1.5,
                  }}
                >
                  {before}
                  {after !== null && (
                    <>
                      <span
                        style={{
                          background: badgeBg,
                          color: accent,
                          fontWeight: 700,
                          padding: "0 2px",
                          borderRadius: "3px",
                        }}
                      >
                        {mock.highlight}
                      </span>
                      {after}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function AppPreviewCard({ app, mock, accent, badgeBg }) {
  const Icon = app.icon;
  return (
    <div
      className="app-scroll-card"
      style={{
        width: "252px",
        flex: "0 0 auto",
        scrollSnapAlign: "start",
        background: "#FFFFFF",
        borderRadius: "24px",
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(24,22,17,0.06)",
      }}
    >
      <div
        style={{
          background: badgeBg,
          padding: "26px 0 20px",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div className="phone-mock" style={{ transform: "rotate(-2.5deg)" }}>
          <PhoneMockup mock={mock} accent={accent} badgeBg={badgeBg} />
        </div>
      </div>

      <div style={{ padding: "18px 20px 20px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "10px",
          }}
        >
          <div
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "11px",
              background: badgeBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              transform: "rotate(-4deg)",
            }}
          >
            <Icon size={18} color={accent} strokeWidth={2} />
          </div>
          <div
            style={{
              fontFamily: "'Baloo 2', sans-serif",
              fontWeight: 700,
              fontSize: "16.5px",
              color: INK,
              lineHeight: 1.2,
            }}
          >
            {app.name}
          </div>
        </div>

        <p
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "12.5px",
            color: MUTED,
            lineHeight: 1.55,
            margin: "0 0 14px",
            minHeight: "38px",
          }}
        >
          {app.description}
        </p>

        <div
          style={{
            borderTop: `1.5px dashed ${LINE}`,
            paddingTop: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
          }}
        >
          <a
            href={app.android}
            target="_blank"
            rel="noopener noreferrer"
            className="gp-pill"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              background: INK,
              color: CREAM,
              padding: "9px 15px",
              borderRadius: "999px",
              textDecoration: "none",
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 600,
              fontSize: "12px",
              transition: "transform 0.15s ease, box-shadow 0.15s ease",
            }}
          >
            <PlayCircle size={14} />
            Get the app
            <ArrowRight size={12} />
          </a>
        </div>

        <div
          style={{
            marginTop: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "5px",
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "11px",
            color: "#B3AC98",
          }}
        >
          <Apple size={11} />
          iOS coming soon
        </div>
      </div>
    </div>
  );
}
/* ------------------------------------------------------------------ */
/*  Desktop / Agent card                                               */
/* ------------------------------------------------------------------ */

function DesktopCard({ app, tone }) {
  const Icon = app.icon;
  const bg = tone === "jade" ? MINT : "#FCE9E0";
  const stroke = tone === "jade" ? FOREST : ORANGE_DEEP;
  const accentBar = tone === "jade" ? FOREST : ORANGE;
  const isJamb = app.name.toLowerCase().includes("jamb");
  const dl = isJamb
    ? {
        windows:
          "https://downloads.kinteron.com/jamb/1.3.2/KINTERON.JAMB.CBT_1.3.2_x64-setup.exe",
        mac: "https://downloads.kinteron.com/jamb/1.3.2/KINTERON.JAMB.CBT_1.3.2_universal.dmg",
      }
    : {
        windows:
          "https://downloads.kinteron.com/waec/1.1.5/KINTERON.WAEC.CBT_1.1.5_x64-setup.exe",
        mac: "https://downloads.kinteron.com/waec/1.1.5/KINTERON.WAEC.CBT_1.1.5_universal.dmg",
      };
  return (
    <div
      className="desktop-app-card"
      style={{
        background: "#FFFFFF",
        borderRadius: "22px",
        padding: "30px 26px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "6px",
          background: accentBar,
        }}
      />
      <div
        style={{
          width: "52px",
          height: "52px",
          borderRadius: "16px",
          background: bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "18px",
        }}
      >
        <Icon size={24} color={stroke} strokeWidth={1.7} />
      </div>

      <div
        style={{
          fontFamily: "'Baloo 2', sans-serif",
          fontWeight: 700,
          fontSize: "23px",
          color: INK,
          marginBottom: "10px",
        }}
      >
        {app.name}
      </div>

      <p
        style={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: "13.5px",
          color: MUTED,
          lineHeight: 1.65,
          margin: "0 0 18px",
        }}
      >
        {app.description}
      </p>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          marginBottom: "22px",
        }}
      >
        {app.features.map((f) => (
          <div
            key={f}
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <CheckCircle2 size={14} color={stroke} strokeWidth={2} />
            <span
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "12.5px",
                color: INK,
                fontWeight: 500,
              }}
            >
              {f}
            </span>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <div
          style={{
            display: "flex",
            gap: "8px",
          }}
        >
          <a
            href={dl.windows}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              flex: 1,
              background: accentBar,
              color: "#FFFFFF",
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 600,
              fontSize: "12.5px",
              padding: "12px 10px",
              borderRadius: "12px",
              textDecoration: "none",
            }}
          >
            <Monitor size={14} />
            Windows
          </a>
          <a
            href={dl.mac}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              flex: 1,
              background: bg,
              color: stroke,
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 600,
              fontSize: "12.5px",
              padding: "12px 10px",
              borderRadius: "12px",
              textDecoration: "none",
            }}
          >
            <Monitor size={14} />
            Mac
          </a>
        </div>
        <a
          href="/desktop"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: "100%",
            background: INK,
            color: CREAM,
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 600,
            fontSize: "13.5px",
            padding: "13px 16px",
            borderRadius: "12px",
            textDecoration: "none",
          }}
          className="desktop-cta"
        >
          <Monitor size={15} />
          Full details & download
        </a>
        <a
          href={`https://wa.me/2347044257292?text=${encodeURIComponent(
            `Hello, I'd like to buy one copy of ${app.name} for my own computer`,
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: "100%",
            background: "#25D366",
            color: "#FFFFFF",
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 600,
            fontSize: "13.5px",
            padding: "13px 16px",
            borderRadius: "12px",
            textDecoration: "none",
          }}
          className="desktop-cta"
        >
          <Monitor size={15} />
          Buy activation — ₦2,000
        </a>
        <a
          href="/reseller"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            width: "100%",
            background: "transparent",
            color: stroke,
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 600,
            fontSize: "12.5px",
            padding: "8px 12px",
            borderRadius: "10px",
            textDecoration: "none",
          }}
          className="desktop-cta-secondary"
        >
          Buying in bulk? Become a reseller
          <ArrowRight size={12} />
        </a>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

export default function KinteronLanding() {
  const [navScrolled, setNavScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [premiumOpen, setPremiumOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [paymentReturnEmail, setPaymentReturnEmail] = useState("");
  const appsRef = useRef(null);
  const desktopRef = useRef(null);
  const storeRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Handle the browser returning from Paystack checkout, or a direct
  // deep link into the payment modal with a pre-filled email.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("paystack") === "return") {
      setPaymentReturnEmail(params.get("email") || "");
      setSuccessOpen(true);
      // clean the URL so a refresh doesn't reopen the modal
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("email")) {
      setPremiumOpen(true);
    }
  }, []);

  const scrollTo = (ref) => {
    setMobileMenuOpen(false);
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const DESKTOP_APPS = [
    {
      name: "JAMB Desktop CBT",
      icon: Brain,
      description:
        "A standalone Windows and Mac installer with the full JAMB question bank — same interface as the real UTME.",
      features: [
        "2025 CBT questions included",
        "Official JAMB Novel summary",
        "Official JAMB calculator & exam keys",
      ],
    },
    {
      name: "WAEC Desktop CBT",
      icon: GraduationCap,
      description:
        "A separate Windows and Mac installer for WAEC — Objective and Theory past questions.",
      features: [
        "WAEC & WASSCE Theory + Objectives",
        "Latest WAEC syllabus for 2027 admissions",
        "Bookmark questions to study later",
      ],
    },
  ];

  const DESKTOP_CHECKLIST = [
    "2025 CBT questions included",
    "Latest JAMB & WAEC syllabus for 2027 admissions",
    "Official JAMB Novel summary included",
    "Activate once, use for the life of your computer — very cheap",
    "90,000+ offline past questions with solutions, for JAMB and WAEC",
    "Works fully offline, even in remote and rural areas",
    "Study past questions year by year, from 2000 to 2026",
    "Bookmark questions to study later",
    "Exam history and performance analysis",
    "Review all your past exam attempts",
    "WAEC & WASSCE Theory and Objectives included",
    "Official JAMB calculator included",
    "Practice using official JAMB keys — arrow keys, A/B/C/D, S to submit",
    "Carry every subject's past questions on one computer",
    "Practice the exact CBT environment with custom settings",
    "AI answer verifier on every question",
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@500;600;700;800&family=Space+Grotesk:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { overflow-x: hidden; }
        body { background: ${CREAM}; overflow-x: hidden; max-width: 100vw; }
        a { -webkit-tap-highlight-color: transparent; }

        :focus-visible { outline: 2.5px solid ${INDIGO}; outline-offset: 3px; }

        @media (prefers-reduced-motion: no-preference) {
          @keyframes floaty {
            0%, 100% { transform: translateY(0) rotate(-1.6deg); }
            50% { transform: translateY(-12px) rotate(-1.6deg); }
          }
          @keyframes spinSlow {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes marquee {
            from { transform: translateX(0); }
            to { transform: translateX(-50%); }
          }
          @keyframes twinkle {
            0%, 100% { opacity: 0.35; transform: scale(0.85) rotate(0deg); }
            50% { opacity: 1; transform: scale(1.1) rotate(12deg); }
          }
          .twinkle-slow { animation: twinkle 3.2s ease-in-out infinite; }
          .twinkle-fast { animation: twinkle 2.1s ease-in-out infinite; animation-delay: 0.8s; }
          .float-card { animation: floaty 6.5s ease-in-out infinite; }
          .spin-slow { animation: spinSlow 18s linear infinite; }
        }

        .cta-solid {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          background: ${ORANGE};
          color: #FFFFFF;
          font-family: 'Space Grotesk', sans-serif;
          font-weight: 700;
          font-size: 15px;
          padding: 15px 28px;
          border-radius: 999px;
          border: none;
          cursor: pointer;
          text-decoration: none;
          box-shadow: 0 5px 0 ${ORANGE_DEEP};
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .cta-solid:hover { transform: translateY(2px); box-shadow: 0 3px 0 ${ORANGE_DEEP}; }
        .cta-solid:active { transform: translateY(5px); box-shadow: 0 0 0 ${ORANGE_DEEP}; }

        .cta-blue {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          background: ${INDIGO};
          color: #FFFFFF;
          font-family: 'Space Grotesk', sans-serif;
          font-weight: 700;
          font-size: 14.5px;
          padding: 13px 24px;
          border-radius: 999px;
          border: none;
          cursor: pointer;
          text-decoration: none;
          box-shadow: 0 5px 0 ${INDIGO_DEEP};
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .cta-blue:hover { transform: translateY(2px); box-shadow: 0 3px 0 ${INDIGO_DEEP}; }

        .cta-premium {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(135deg, ${PREMIUM_1}, ${PREMIUM_2});
          color: #FFFFFF;
          font-family: 'Space Grotesk', sans-serif;
          font-weight: 700;
          font-size: 14.5px;
          padding: 13px 24px;
          border-radius: 999px;
          border: none;
          cursor: pointer;
          text-decoration: none;
          box-shadow: 0 4px 14px rgba(147,51,234,0.35);
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .cta-premium:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 22px rgba(147,51,234,0.45);
        }
        .hero-premium-banner {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hero-premium-banner:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 30px rgba(147,51,234,0.4);
        }

        .cta-line {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          background: #FFFFFF;
          color: ${INK};
          font-family: 'Space Grotesk', sans-serif;
          font-weight: 700;
          font-size: 14.5px;
          padding: 13px 22px;
          border-radius: 999px;
          border: 1.5px solid ${LINE};
          cursor: pointer;
          text-decoration: none;
          box-shadow: 0 1px 3px rgba(24,22,17,0.06);
          transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
        }
        .cta-line:hover { background: ${CREAM_CARD}; transform: translateY(-2px); box-shadow: 0 6px 14px rgba(24,22,17,0.1); }

        .feature-card {
          box-shadow: 0 1px 2px rgba(24,22,17,0.05);
        }
        .feature-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 14px 28px rgba(24,22,17,0.1);
        }
        .desktop-cta:hover { background: ${INDIGO_DEEP}; }
        .desktop-cta-secondary:hover { background: ${CREAM_CARD}; }

        .nav-tab {
          font-family: 'Space Grotesk', sans-serif;
          font-weight: 600;
          font-size: 14.5px;
          color: ${INK};
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 8px 6px;
        }

        .narrative-grid > * { min-width: 0; }

        .app-feature-card {
          box-shadow: 0 1px 2px rgba(24,22,17,0.04);
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }
        .app-feature-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 14px 28px rgba(24,22,17,0.09);
        }
        .desktop-app-card {
          box-shadow: 0 1px 3px rgba(24,22,17,0.06);
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }
        .desktop-app-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 32px rgba(24,22,17,0.12);
        }

        .app-scroll-row {
  display: flex;
  gap: 16px;
  overflow-x: auto;
  scroll-snap-type: x proximity;
  padding: 4px 4px 14px;
  margin: 0 -4px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: ${MINT_DEEP} transparent;
}
.app-scroll-row::-webkit-scrollbar { height: 6px; }
.app-scroll-row::-webkit-scrollbar-thumb { background: ${MINT_DEEP}; border-radius: 999px; }
.app-scroll-row::-webkit-scrollbar-track { background: transparent; }

        @media (max-width: 940px) {
          .hero-3d-bg { display: none !important; }
          .nav-links { display: none !important; }
          .nav-cta-desktop { display: none !important; }
          .mobile-menu-btn { display: flex !important; }
          .mobile-menu-panel { display: flex !important; }
          .hero-grid { grid-template-columns: 1fr !important; }
          .hero-right { order: -1; margin-bottom: 20px; }
          .hero-left { padding-bottom: 8px !important; }
          .feature-grid { grid-template-columns: 1fr !important; }
          .narrative-grid { grid-template-columns: 1fr !important; }
          .app-list-stores { margin-left: 0 !important; width: 100% !important; }
          .app-list-stores > div { width: 100%; }
          .app-list-stores a, .app-list-stores span { flex: 1 1 0; justify-content: center; }
          .narrative-flip .narrative-media { order: -1 !important; }
          .desktop-grid { grid-template-columns: 1fr !important; }
          .checklist-grid { grid-template-columns: 1fr !important; }
          .map-band { flex-direction: column !important; gap: 22px !important; }
          .app-store-band { flex-direction: column !important; align-items: flex-start !important; gap: 20px !important; }
          .app-store-visual { display: none !important; }
        }
      `}</style>

      <div
        style={{
          background: CREAM,
          minHeight: "100vh",
          color: INK,
          overflowX: "hidden",
        }}
      >
        {/* NAVBAR */}
        <nav
          style={{
            position: "sticky",
            top: 0,
            zIndex: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 24px 0 40px",
            height: "76px",
            background:
              navScrolled || mobileMenuOpen
                ? "rgba(253,248,240,0.96)"
                : "transparent",
            backdropFilter:
              navScrolled || mobileMenuOpen ? "blur(10px)" : "none",
            borderBottom:
              navScrolled || mobileMenuOpen
                ? `1px solid ${LINE}`
                : "1px solid transparent",
            transition: "background 0.25s, border-color 0.25s",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "36px" }}>
            <span
              style={{
                fontFamily: "'Baloo 2', sans-serif",
                fontWeight: 800,
                fontSize: "23px",
                color: INK,
                letterSpacing: "-0.3px",
              }}
            >
              Kinteron
            </span>

            <div
              className="nav-links"
              style={{ display: "flex", alignItems: "center", gap: "26px" }}
            >
              <AppsMenu />
              <a className="nav-tab" href="/desktop" style={{ textDecoration: "none" }}>
                Desktop
              </a>
              <button className="nav-tab" onClick={() => scrollTo(storeRef)}>
                Download
              </button>
              <a className="nav-tab" href="/reseller" style={{ textDecoration: "none" }}>
                Reseller
              </a>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <button
              className="cta-premium nav-cta-desktop"
              onClick={() => setPremiumOpen(true)}
              style={{ padding: "11px 20px", fontSize: "13.5px" }}
            >
              <Zap size={15} fill="#FFFFFF" strokeWidth={0} />
              Unlock Premium
            </button>
            <button
              className="mobile-menu-btn"
              onClick={() => setMobileMenuOpen((o) => !o)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              style={{
                display: "none",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                borderRadius: "12px",
                border: "none",
                background: mobileMenuOpen ? INK : CREAM_CARD,
                color: mobileMenuOpen ? CREAM : INK,
                cursor: "pointer",
              }}
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </nav>

        {/* MOBILE MENU PANEL */}
        {mobileMenuOpen && (
          <div
            className="mobile-menu-panel"
            style={{
              position: "sticky",
              top: "76px",
              zIndex: 99,
              background: "#FFFFFF",
              borderBottom: `1px solid ${LINE}`,
              padding: "8px 20px 20px",
              display: "none",
              flexDirection: "column",
              gap: "4px",
              boxShadow: "0 12px 24px rgba(24,22,17,0.08)",
            }}
          >
            {APPS.map((app) => {
              const Icon = app.icon;
              return (
                <div
                  key={app.name}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 8px",
                    borderBottom: `1px solid ${LINE}`,
                  }}
                >
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "10px",
                      background: MINT,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={17} color={FOREST} strokeWidth={1.8} />
                  </div>
                  <span
                    style={{
                      flex: 1,
                      fontFamily: "'Baloo 2', sans-serif",
                      fontWeight: 600,
                      fontSize: "15px",
                      color: INK,
                    }}
                  >
                    {app.name}
                  </span>
                  <a
                    href={app.android}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      background: MINT,
                      color: FOREST,
                      flexShrink: 0,
                    }}
                  >
                    <PlayCircle size={15} strokeWidth={1.9} />
                  </a>
                </div>
              );
            })}
            <a
              className="nav-tab"
              href="/desktop"
              style={{ textAlign: "left", padding: "14px 8px", textDecoration: "none" }}
            >
              Desktop CBT programs
            </a>
            <button
              className="nav-tab"
              onClick={() => scrollTo(storeRef)}
              style={{ textAlign: "left", padding: "14px 8px" }}
            >
              Download the apps
            </button>
            <a
              href="/reseller"
              className="nav-tab"
              style={{ textAlign: "left", padding: "14px 8px", textDecoration: "none" }}
            >
              Become a Reseller
            </a>
            <button
              className="cta-premium"
              onClick={() => {
                setMobileMenuOpen(false);
                setPremiumOpen(true);
              }}
              style={{ justifyContent: "center", marginTop: "8px" }}
            >
              <Zap size={15} fill="#FFFFFF" strokeWidth={0} />
              Unlock Premium
            </button>
          </div>
        )}

        {/* HERO */}
        <section
          style={{
            background: `linear-gradient(180deg, ${PEACH_1} 0%, ${PEACH_2} 100%)`,
            padding: "56px 40px 0",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* decorative sky graphics */}
          <Cloud width={150} style={{ top: "30px", left: "4%" }} />
          <Cloud width={90} opacity={0.6} style={{ top: "120px", right: "8%" }} />
          <Cloud width={70} opacity={0.5} style={{ bottom: "180px", left: "12%" }} />
          <StarBurst size={26} color="#FFFFFF" className="twinkle-slow" style={{ top: "60px", left: "22%" }} />
          <StarBurst size={20} color="#FFFFFF" className="twinkle-fast" style={{ top: "200px", left: "2%" }} />
          <SparkleDoodle size={22} color="#D9642A" className="twinkle-slow" style={{ top: "90px", right: "24%" }} />
          <SparkleDoodle size={16} color="#FFFFFF" className="twinkle-fast" style={{ bottom: "220px", right: "3%" }} />

          {/* 3D backdrop — ambient layer behind everything, never blocks clicks */}
          <div
            className="hero-3d-bg"
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              width: "58%",
              pointerEvents: "none",
              zIndex: 0,
            }}
          >
            <WebGLErrorBoundary>
              <Hero3D />
            </WebGLErrorBoundary>
          </div>

          <div
            style={{
              maxWidth: "1220px",
              margin: "0 auto",
              display: "grid",
              gridTemplateColumns: "1.05fr 0.95fr",
              gap: "40px",
              alignItems: "center",
              position: "relative",
              zIndex: 1,
            }}
            className="hero-grid"
          >
            <div className="hero-left" style={{ paddingBottom: "70px" }}>
              <Pill tone="cream">
                <Sparkles size={13} style={{ marginRight: "6px" }} />5 apps · 1
                mission
              </Pill>

              <h1
                style={{
                  fontFamily: "'Baloo 2', sans-serif",
                  fontWeight: 700,
                  fontSize: "clamp(40px, 5vw, 60px)",
                  lineHeight: 1.08,
                  letterSpacing: "-0.5px",
                  margin: "22px 0 8px",
                  color: INK,
                }}
              >
                Sit the exam
                <br />
                before you sit the{" "}
                <span style={{ position: "relative", display: "inline-block" }}>
                  <HighlightWord bg={INK} color={CREAM}>
                    exam
                  </HighlightWord>
                  <span
                    style={{
                      position: "absolute",
                      left: "8px",
                      bottom: "-14px",
                      pointerEvents: "none",
                    }}
                  >
                    <Squiggle color={ORANGE_DEEP} width={90} />
                  </span>
                </span>
              </h1>

              <p
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "16.5px",
                  color: "#3B3728",
                  lineHeight: 1.65,
                  margin: "22px 0 32px",
                  maxWidth: "440px",
                }}
              >
                Five CBT apps for JAMB, WAEC, NECO and Post-UTME, plus two
                offline desktop programs for home or centre use — with a
                built-in AI tutor, topic-by-topic practice and every answer
                explained, not just marked.
              </p>

              <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
                <button className="cta-solid" onClick={() => scrollTo(appsRef)}>
                  Explore the apps
                  <ArrowRight size={16} />
                </button>
                <button
                  className="cta-line"
                  onClick={() => scrollTo(desktopRef)}
                >
                  <Monitor size={16} />
                  Desktop version
                </button>
              </div>

              <button
                onClick={() => setPremiumOpen(true)}
                className="hero-premium-banner"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  marginTop: "22px",
                  padding: "14px 20px",
                  width: "100%",
                  maxWidth: "460px",
                  background: `linear-gradient(135deg, ${PREMIUM_1}, ${PREMIUM_2})`,
                  border: "none",
                  borderRadius: "16px",
                  cursor: "pointer",
                  boxShadow: "0 8px 24px rgba(147,51,234,0.3)",
                  textAlign: "left",
                }}
              >
                <div
                  style={{
                    width: "38px",
                    height: "38px",
                    borderRadius: "11px",
                    background: "rgba(255,255,255,0.22)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Zap
                    size={19}
                    color="#FFFFFF"
                    fill="#FFFFFF"
                    strokeWidth={0}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontFamily: "'Baloo 2', sans-serif",
                      fontWeight: 700,
                      fontSize: "15px",
                      color: "#FFFFFF",
                    }}
                  >
                    ₦2,000 unlocks all 4 apps, forever
                  </div>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: "12px",
                      color: "rgba(255,255,255,0.85)",
                    }}
                  >
                    One-time payment · JAMB, WAEC, NECO &amp; Post-UTME premium
                  </div>
                </div>
                <ArrowRight
                  size={18}
                  color="#FFFFFF"
                  style={{ flexShrink: 0 }}
                />
              </button>
            </div>

            <div
              className="hero-right"
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
            }}
          >
              <div
                className="float-card"
                style={{
                  width: "min(340px, 100%)",
                  background: "#FFFFFF",
                  borderRadius: "22px",
                  padding: "24px",
                  boxShadow:
                    "0 24px 48px rgba(24,22,17,0.16), 0 8px 20px rgba(24,22,17,0.08)",
                  boxSizing: "border-box",
                  position: "relative",
                }}
              >
                <CapBuddy size={46} style={{ top: "-26px", right: "18px" }} />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                    paddingBottom: "12px",
                    borderBottom: `1.5px dashed ${LINE}`,
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: "11px",
                      color: FOREST,
                      fontWeight: 600,
                    }}
                  >
                    JAMB CBT · Q 23/40
                  </span>
                  <span
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: "11px",
                      color: MUTED,
                    }}
                  >
                    01:24:09
                  </span>
                </div>
                <p
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: "13.5px",
                    color: INK,
                    lineHeight: 1.55,
                    marginBottom: "14px",
                  }}
                >
                  Which of the following is a characteristic of covalent
                  compounds?
                </p>
                {[
                  { opt: "A", text: "High melting points", selected: false },
                  {
                    opt: "B",
                    text: "Conducts electricity in solution",
                    selected: false,
                  },
                  {
                    opt: "C",
                    text: "Low melting and boiling points",
                    selected: true,
                  },
                  {
                    opt: "D",
                    text: "Ionic bonding structure",
                    selected: false,
                  },
                ].map(({ opt, text, selected }) => (
                  <div
                    key={opt}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "9px 12px",
                      borderRadius: "10px",
                      marginBottom: "6px",
                      background: selected ? MINT : "#FAF8F2",
                      border: selected
                        ? `1.5px solid ${FOREST}`
                        : `1.5px solid ${LINE}`,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: "11px",
                        fontWeight: 700,
                        color: selected ? FOREST : "#B3AC98",
                        minWidth: "14px",
                      }}
                    >
                      {opt}
                    </span>
                    <span
                      style={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: "12px",
                        color: selected ? INK : "#6B664F",
                      }}
                    >
                      {text}
                    </span>
                    {selected && (
                      <CheckCircle2
                        size={13}
                        color={FOREST}
                        strokeWidth={2.4}
                        style={{ marginLeft: "auto" }}
                      />
                    )}
                  </div>
                ))}
                <div
                  style={{
                    marginTop: "12px",
                    padding: "10px 12px",
                    borderRadius: "10px",
                    background: MINT,
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: "11px",
                      color: FOREST,
                      fontWeight: 500,
                    }}
                  >
                    Correct — weak intermolecular forces give low
                    melting/boiling points.
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* wave divider into mint section */}
          <svg
            viewBox="0 0 1440 80"
            preserveAspectRatio="none"
            style={{
              width: "100%",
              height: "60px",
              display: "block",
              marginTop: "50px",
            }}
          >
            <path
              d="M0,40 C240,90 480,0 720,20 C960,40 1200,90 1440,30 L1440,80 L0,80 Z"
              fill={MINT}
            />
          </svg>
        </section>

        {/* MARQUEE STRIP */}
        <Marquee
          items={[
            "JAMB CBT",
            "WAEC CBT",
            "NECO CBT",
            "POST-UTME",
            "KINTERNOTES",
            "AI-VERIFIED ANSWERS",
            "90,000+ PAST QUESTIONS",
          ]}
        />

        {/* FEATURES */}
        <section style={{ background: MINT, padding: "10px 40px 90px" }}>
          <div style={{ maxWidth: "1180px", margin: "0 auto" }}>
            <Reveal>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                  gap: "24px",
                  flexWrap: "wrap",
                  marginBottom: "40px",
                }}
              >
                <div>
                  <h2
                    style={{
                      fontFamily: "'Baloo 2', sans-serif",
                      fontWeight: 700,
                      fontSize: "clamp(28px, 3.6vw, 42px)",
                      color: INK,
                      lineHeight: 1.12,
                      letterSpacing: "-0.4px",
                    }}
                  >
                    Built for the way
                    <br />
                    Nigerian students revise.
                  </h2>
                </div>
                <p
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: "14.5px",
                    color: "#33512F",
                    maxWidth: "320px",
                    lineHeight: 1.6,
                  }}
                >
                  Not affiliated with JAMB or any government examination body —
                  just tools built to prepare you for them.
                </p>
              </div>
            </Reveal>

            <div
              className="feature-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "22px",
              }}
            >
              {FEATURE_CARDS.map(({ icon: Icon, title, body }, i) => (
                <Reveal key={title} delay={i * 90}>
                  <div
                    className="feature-card"
                    style={{
                      background: CREAM_CARD,
                      borderRadius: "20px",
                      padding: "26px 24px",
                      position: "relative",
                      height: "100%",
                      transition: "transform 0.25s ease, box-shadow 0.25s ease",
                    }}
                  >
                    <CornerMark
                      color={i === 0 ? ORANGE : i === 1 ? INDIGO : FOREST}
                    />
                    <div
                      style={{
                        width: "46px",
                        height: "46px",
                        borderRadius: "13px",
                        background: "#FFFFFF",
                        border: `1.5px solid ${LINE}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "18px",
                      }}
                    >
                      <Icon size={21} color={INK} strokeWidth={1.7} />
                    </div>
                    <h3
                      style={{
                        fontFamily: "'Baloo 2', sans-serif",
                        fontWeight: 600,
                        fontSize: "18.5px",
                        color: INK,
                        marginBottom: "10px",
                        lineHeight: 1.25,
                      }}
                    >
                      {title}
                    </h3>
                    <p
                      style={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: "13.5px",
                        color: MUTED,
                        lineHeight: 1.6,
                      }}
                    >
                      {body}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* NARRATIVE 1 — apps, full-width scroll strip */}
        <section ref={appsRef} style={{ padding: "90px 40px" }}>
          <div style={{ maxWidth: "1180px", margin: "0 auto" }}>
            <Reveal>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                  gap: "24px",
                  flexWrap: "wrap",
                  marginBottom: "36px",
                }}
              >
                <div>
                  <span
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: "12px",
                      color: ORANGE_DEEP,
                      letterSpacing: "0.06em",
                    }}
                  >
                    ONE APP PER EXAM
                  </span>
                  <h2
                    style={{
                      fontFamily: "'Baloo 2', sans-serif",
                      fontWeight: 700,
                      fontSize: "clamp(28px, 3.6vw, 42px)",
                      color: INK,
                      margin: "12px 0 0",
                      lineHeight: 1.14,
                      letterSpacing: "-0.4px",
                    }}
                  >
                    Five apps,
                    <br />
                    each one{" "}
                    <HighlightWord bg={FOREST} color={CREAM} rotate="1deg">
                      focused
                    </HighlightWord>
                  </h2>
                </div>
                <p
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: "14.5px",
                    color: MUTED,
                    maxWidth: "320px",
                    lineHeight: 1.6,
                  }}
                >
                  Nothing generic — every app is built around one exam's
                  syllabus, format and past questions. Swipe to see them all.
                </p>
              </div>
            </Reveal>

            <Reveal delay={90}>
              <div className="app-scroll-row">
                {APPS.map((app, i) => {
                  const accent = [
                    ORANGE,
                    INDIGO,
                    FOREST,
                    ORANGE_DEEP,
                    INDIGO_DEEP,
                  ][i % 5];
                  const badgeBg = [
                    "#FDE6D4",
                    "#E1E4F5",
                    "#DCEEE3",
                    "#FBE3D2",
                    "#E3E5F5",
                  ][i % 5];
                  return (
                    <AppPreviewCard
                      key={app.name}
                      app={app}
                      mock={APP_MOCKUPS[i]}
                      accent={accent}
                      badgeBg={badgeBg}
                    />
                  );
                })}
              </div>
            </Reveal>

            <Reveal delay={150}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "28px",
                }}
              >
                <button
                  className="cta-solid"
                  onClick={() => scrollTo(storeRef)}
                >
                  Download the apps
                  <ArrowRight size={16} />
                </button>
              </div>
            </Reveal>
          </div>
        </section>

        {/* HOW IT WORKS — journey steps */}
        <section style={{ background: CREAM, padding: "90px 40px" }}>
          <div style={{ maxWidth: "1180px", margin: "0 auto", position: "relative" }}>
            <NigeriaMap width={170} style={{ top: "-30px", right: "2%" }} />
            <SparkleDoodle size={24} color={INDIGO} className="twinkle-slow" style={{ top: "10px", right: "18%" }} />
            <Reveal>
              <div style={{ textAlign: "center", marginBottom: "52px" }}>
                <span
                  style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: "12px",
                    color: ORANGE_DEEP,
                    letterSpacing: "0.06em",
                  }}
                >
                  THE KINTERON JOURNEY
                </span>
                <h2
                  style={{
                    fontFamily: "'Baloo 2', sans-serif",
                    fontWeight: 700,
                    fontSize: "clamp(28px, 3.8vw, 44px)",
                    color: INK,
                    margin: "12px auto 0",
                    lineHeight: 1.15,
                    maxWidth: "620px",
                  }}
                >
                  From first practice to{" "}
                  <HighlightWord bg={ORANGE} color="#FFFFFF" rotate="-1deg">
                    exam day
                  </HighlightWord>
                </h2>
              </div>
            </Reveal>

            <div
              className="feature-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "22px",
              }}
            >
              {[
                {
                  icon: PencilLine,
                  num: "1.",
                  title: "Download an app",
                  body: "Pick your exam — JAMB, WAEC, NECO or Post-UTME — and start practising free on Google Play.",
                  tone: ORANGE,
                },
                {
                  icon: Monitor,
                  num: "2.",
                  title: "Practise the real thing",
                  body: "Sit full CBT simulations with the official timer, calculator and exam keys.",
                  tone: INDIGO,
                },
                {
                  icon: LineChart,
                  num: "3.",
                  title: "Learn from every answer",
                  body: "AI verifies and explains each question so you understand the reasoning, not just the score.",
                  tone: FOREST,
                },
                {
                  icon: BadgeCheck,
                  num: "4.",
                  title: "Walk in confident",
                  body: "Track your history, fix weak topics, and sit the real exam like it's just another practice run.",
                  tone: INDIGO_DEEP,
                },
              ].map(({ icon: Icon, num, title, body, tone }, i) => (
                <Reveal key={num} delay={i * 100}>
                  <div
                    className="app-feature-card"
                    style={{
                      background: "#FFFFFF",
                      borderRadius: "22px",
                      padding: "28px 24px",
                      position: "relative",
                      height: "100%",
                    }}
                  >
                    <Blob
                      width={120}
                      opacity={0.35}
                      style={{ bottom: "-20px", right: "-24px" }}
                    />
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "14px",
                        background: tone,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "16px",
                        boxShadow: `0 6px 14px ${tone}55`,
                      }}
                    >
                      <Icon size={22} color="#FFFFFF" strokeWidth={1.9} />
                    </div>
                    <div
                      style={{
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: tone,
                        marginBottom: "6px",
                      }}
                    >
                      {num}
                    </div>
                    <h3
                      style={{
                        fontFamily: "'Baloo 2', sans-serif",
                        fontWeight: 600,
                        fontSize: "18px",
                        color: INK,
                        marginBottom: "8px",
                      }}
                    >
                      {title}
                    </h3>
                    <p
                      style={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: "13px",
                        color: MUTED,
                        lineHeight: 1.6,
                      }}
                    >
                      {body}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* DESKTOP TAB SECTION */}
        <section
          ref={desktopRef}
          style={{ background: MINT, padding: "90px 40px" }}
        >
          <div style={{ maxWidth: "1180px", margin: "0 auto" }}>
            <Reveal>
              <div style={{ textAlign: "center", marginBottom: "48px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                    marginBottom: "14px",
                  }}
                >
                  <Pill tone="ink">2026 UTME Ready</Pill>
                  <Pill tone="mint">2025 CBT questions included</Pill>
                </div>
                <span
                  style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: "12px",
                    color: "#33512F",
                    letterSpacing: "0.06em",
                  }}
                >
                  FOR STUDENTS, PARENTS &amp; TUTORIAL CENTRES
                </span>
                <h2
                  style={{
                    fontFamily: "'Baloo 2', sans-serif",
                    fontWeight: 700,
                    fontSize: "clamp(28px, 3.6vw, 42px)",
                    color: INK,
                    margin: "12px auto 0",
                    lineHeight: 1.15,
                    maxWidth: "560px",
                  }}
                >
                  Two desktop CBT programs,{" "}
                  <HighlightWord bg={ORANGE} color="#FFFFFF" rotate="-1deg">
                    built to run offline
                  </HighlightWord>
                </h2>
                <p
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: "15px",
                    color: "#3B3728",
                    lineHeight: 1.7,
                    margin: "18px auto 0",
                    maxWidth: "460px",
                  }}
                >
                  Independent Windows and Mac installers for JAMB and WAEC — buy
                  one copy for your own computer, or set up a full CBT centre as
                  an agent or reseller.
                </p>
              </div>
            </Reveal>

            <div
              className="desktop-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "24px",
                marginBottom: "32px",
              }}
            >
              <Reveal>
                <DesktopCard app={DESKTOP_APPS[0]} tone="jade" />
              </Reveal>
              <Reveal delay={90}>
                <DesktopCard app={DESKTOP_APPS[1]} tone="orange" />
              </Reveal>
            </div>

            <Reveal delay={120}>
              <div
                style={{
                  background: "#FFFFFF",
                  borderRadius: "22px",
                  padding: "32px 34px",
                  marginBottom: "24px",
                  boxShadow: "0 1px 3px rgba(24,22,17,0.06)",
                }}
              >
                <div
                  style={{
                    fontFamily: "'Baloo 2', sans-serif",
                    fontWeight: 700,
                    fontSize: "clamp(19px, 2.2vw, 23px)",
                    color: INK,
                    marginBottom: "20px",
                  }}
                >
                  What's included, in full
                </div>
                <div
                  className="checklist-grid"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    columnGap: "28px",
                    rowGap: "13px",
                  }}
                >
                  {DESKTOP_CHECKLIST.map((item) => (
                    <div
                      key={item}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "9px",
                      }}
                    >
                      <CheckCircle2
                        size={16}
                        color={FOREST}
                        strokeWidth={2}
                        style={{ flexShrink: 0, marginTop: "1.5px" }}
                      />
                      <span
                        style={{
                          fontFamily: "'Space Grotesk', sans-serif",
                          fontSize: "13.5px",
                          color: "#33321f",
                          lineHeight: 1.5,
                        }}
                      >
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
                <p
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: "13px",
                    color: MUTED,
                    fontStyle: "italic",
                    marginTop: "22px",
                    paddingTop: "18px",
                    borderTop: `1px solid ${LINE}`,
                  }}
                >
                  With constant practice on Kinteron JAMB CBT, you're set up to
                  score 300+ in your JAMB CBT exam.
                </p>
              </div>
            </Reveal>

            <Reveal delay={150}>
              <div
                style={{
                  background: "#FFFFFF",
                  border: `2px dashed ${MINT_DEEP}`,
                  borderRadius: "18px",
                  padding: "22px 26px",
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                  boxShadow: "0 1px 3px rgba(24,22,17,0.05)",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "14px" }}
                >
                  <div
                    style={{
                      width: "44px",
                      height: "44px",
                      borderRadius: "12px",
                      background: CREAM_CARD,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <KeyRound size={20} color={ORANGE_DEEP} strokeWidth={1.8} />
                  </div>
                  <div>
                    <div
                      style={{
                        fontFamily: "'Baloo 2', sans-serif",
                        fontWeight: 600,
                        fontSize: "15.5px",
                        color: INK,
                      }}
                    >
                      Activation codes, not accounts
                    </div>
                    <div
                      style={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: "12.5px",
                        color: MUTED,
                      }}
                    >
                      e.g. KINT-XXXX-XXXX-XXXX — one printed code per machine.
                    </div>
                  </div>
                </div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <WifiOff size={16} color={FOREST} strokeWidth={1.8} />
                  <span
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: "12.5px",
                      color: "#33512F",
                      fontWeight: 500,
                    }}
                  >
                    Works fully offline
                  </span>
                  <School
                    size={16}
                    color={FOREST}
                    strokeWidth={1.8}
                    style={{ marginLeft: "10px" }}
                  />
                  <span
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: "12.5px",
                      color: "#33512F",
                      fontWeight: 500,
                    }}
                  >
                    Made for centres
                  </span>
                </div>
              </div>
            </Reveal>

            <Reveal delay={170}>
              <div style={{ display: "flex", justifyContent: "center", marginTop: "28px" }}>
                <a href="/desktop" className="cta-solid" style={{ textDecoration: "none" }}>
                  Open the desktop download page
                  <ArrowRight size={16} />
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        {/* COMMUNITY / AGENT BAND */}
        <section
          style={{
            background: MINT,
            padding: "90px 40px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <NigeriaMap width={220} color="#FFFFFF" style={{ top: "40px", left: "-30px", position: "absolute" }} />
          <StarBurst size={24} color="#FFFFFF" className="twinkle-slow" style={{ top: "60px", right: "10%" }} />
          <SparkleDoodle size={20} color="#2F6B4F" className="twinkle-fast" style={{ bottom: "80px", left: "8%" }} />
          <div
            style={{
              maxWidth: "1000px",
              margin: "0 auto",
              textAlign: "center",
            }}
          >
            <Reveal>
              <h2
                style={{
                  fontFamily: "'Baloo 2', sans-serif",
                  fontWeight: 700,
                  fontSize: "clamp(28px, 3.8vw, 44px)",
                  color: INK,
                  lineHeight: 1.15,
                  marginBottom: "16px",
                }}
              >
                Join the biggest network of
                <br />
                Nigerian exam prep{" "}
                <HighlightWord bg={INDIGO} color="#FFFFFF" rotate="-1deg">
                  centres
                </HighlightWord>
              </h2>
              <p
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "15px",
                  color: "#3B3728",
                  maxWidth: "480px",
                  margin: "0 auto 36px",
                  lineHeight: 1.7,
                }}
              >
                100,000+ students preparing across the country, with agents and
                resellers running CBT centres in their own communities.
              </p>
            </Reveal>

            <Reveal delay={90}>
              <div
                className="map-band"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "60px",
                  flexWrap: "wrap",
                  marginBottom: "36px",
                }}
              >
                {[
                  { num: 100000, suffix: "+", label: "Students preparing" },
                  { num: 90000, suffix: "+", label: "Past questions" },
                  { num: 5, suffix: "", label: "Mobile apps" },
                  { num: 2, suffix: "", label: "Desktop CBT programs" },
                ].map(({ num, suffix, label }) => (
                  <div key={label}>
                    <div
                      style={{
                        fontFamily: "'Baloo 2', sans-serif",
                        fontWeight: 700,
                        fontSize: "clamp(30px, 3.6vw, 44px)",
                        color: INK,
                      }}
                    >
                      <CountUp to={num} suffix={suffix} />
                    </div>
                    <div
                      style={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: "13px",
                        color: "#33512F",
                      }}
                    >
                      {label}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={150}>
              <a
                className="cta-solid"
                href="/reseller"
                style={{ textDecoration: "none" }}
              >
                Become a reseller
                <ArrowRight size={16} />
              </a>
            </Reveal>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section style={{ background: CREAM, padding: "90px 40px", position: "relative", overflow: "hidden" }}>
          <Cloud width={110} opacity={0.5} color={CREAM_CARD} style={{ top: "40px", right: "6%" }} />
          <StarBurst size={22} color={PEACH_2} className="twinkle-fast" style={{ bottom: "60px", left: "4%" }} />
          <div style={{ maxWidth: "1180px", margin: "0 auto" }}>
            <Reveal>
              <div style={{ textAlign: "center", marginBottom: "48px" }}>
                <span
                  style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: "12px",
                    color: ORANGE_DEEP,
                    letterSpacing: "0.06em",
                  }}
                >
                  HEAR FROM STUDENTS
                </span>
                <h2
                  style={{
                    fontFamily: "'Baloo 2', sans-serif",
                    fontWeight: 700,
                    fontSize: "clamp(28px, 3.8vw, 44px)",
                    color: INK,
                    margin: "12px auto 0",
                    lineHeight: 1.15,
                  }}
                >
                  They practised here first.
                  <br />
                  Then they{" "}
                  <HighlightWord bg={FOREST} color={CREAM} rotate="1deg">
                    passed
                  </HighlightWord>
                </h2>
              </div>
            </Reveal>

            <div
              className="feature-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "22px",
              }}
            >
              {[
                {
                  quote:
                    "The CBT interface felt exactly like the real UTME. On exam day I wasn't nervous at all — I already knew where every button was.",
                  name: "Chiamaka O.",
                  detail: "Scored 296 · JAMB 2025",
                  bg: "#FDE6D4",
                  accent: ORANGE_DEEP,
                  initials: "CO",
                },
                {
                  quote:
                    "What helped me most was the AI explanation on every question. I finally understood why answers were wrong, not just that they were wrong.",
                  name: "Ibrahim S.",
                  detail: "WAEC 2025 · 8 distinctions",
                  bg: "#E1E4F5",
                  accent: INDIGO,
                  initials: "IS",
                },
                {
                  quote:
                    "Our centre in Jos runs entirely on Kinteron desktop. It works offline, so even when network fails, classes never stop.",
                  name: "Mrs. Ekwueme",
                  detail: "CBT centre owner · Plateau",
                  bg: "#DCEEE3",
                  accent: FOREST,
                  initials: "AE",
                },
              ].map(({ quote, name, detail, bg, accent, initials }, i) => (
                <Reveal key={name} delay={i * 100}>
                  <div
                    className="app-feature-card"
                    style={{
                      background: "#FFFFFF",
                      borderRadius: "22px",
                      padding: "28px 26px",
                      position: "relative",
                      height: "100%",
                    }}
                  >
                    <CornerMark color={accent} />
                    <p
                      style={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: "14px",
                        color: INK,
                        lineHeight: 1.7,
                        marginBottom: "20px",
                      }}
                    >
                      “{quote}”
                    </p>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          width: "42px",
                          height: "42px",
                          borderRadius: "50%",
                          background: bg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          fontFamily: "'Baloo 2', sans-serif",
                          fontWeight: 700,
                          fontSize: "14px",
                          color: accent,
                        }}
                      >
                        {initials}
                      </div>
                      <div>
                        <div
                          style={{
                            fontFamily: "'Baloo 2', sans-serif",
                            fontWeight: 600,
                            fontSize: "14.5px",
                            color: INK,
                          }}
                        >
                          {name}
                        </div>
                        <div
                          style={{
                            fontFamily: "'Space Grotesk', sans-serif",
                            fontSize: "12px",
                            color: MUTED,
                          }}
                        >
                          {detail}
                        </div>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ — mirrors the FAQPage structured data in index.html */}
        <section style={{ background: MINT, padding: "90px 40px" }}>
          <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
            <Reveal>
              <div style={{ textAlign: "center", marginBottom: "44px" }}>
                <span
                  style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: "12px",
                    color: "#33512F",
                    letterSpacing: "0.06em",
                  }}
                >
                  QUESTIONS STUDENTS ASK
                </span>
                <h2
                  style={{
                    fontFamily: "'Baloo 2', sans-serif",
                    fontWeight: 700,
                    fontSize: "clamp(28px, 3.8vw, 42px)",
                    color: INK,
                    margin: "12px auto 0",
                    lineHeight: 1.15,
                  }}
                >
                  Everything you're{" "}
                  <HighlightWord bg={INDIGO} color="#FFFFFF" rotate="-1deg">
                    wondering
                  </HighlightWord>
                </h2>
              </div>
            </Reveal>

            <div
              className="checklist-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "18px",
              }}
            >
              {[
                {
                  q: "Which app is the best JAMB CBT practice app in Nigeria?",
                  a: "Kinteron JAMB CBT is one of the best JAMB CBT apps in Nigeria. It simulates the exact UTME interface with the official timer, calculator and exam keys, includes past questions from 2000 to 2026, offers topic-by-topic practice and has a built-in AI tutor.",
                },
                {
                  q: "Is there a JAMB app with an AI tutor?",
                  a: "Yes — every Kinteron app (JAMB, WAEC, NECO and Post-UTME) has an AI tutor built in. It doesn't just mark your answers; it verifies them and teaches the reasoning behind each one.",
                },
                {
                  q: "Can I practise JAMB topics one by one instead of full exams?",
                  a: "Yes. Kinteron JAMB CBT supports topic-by-topic practice across all subjects, alongside full subject simulations that mirror the real UTME.",
                },
                {
                  q: "Where can I get WAEC past questions with answers explained?",
                  a: "The Kinteron WAEC CBT app includes WAEC and WASSCE Objective and Theory past questions by year, with every answer checked and explained by AI, mapped to the latest syllabus.",
                },
                {
                  q: "Can I revisit exams I have already taken?",
                  a: "Yes. Kinteron keeps your full exam history — replay any past attempt question by question, see correct answers with AI explanations and review your performance analytics.",
                },
                {
                  q: "Is there a free JAMB CBT app?",
                  a: "Yes. All Kinteron apps are free on Google Play and the latest exam year is always free. A one-time ₦2,000 unlocks all 25+ years across JAMB, WAEC, NECO and Post-UTME — or invite five friends to unlock everything free.",
                },
                {
                  q: "Do Kinteron apps work offline?",
                  a: "Yes. Kinteron also offers standalone desktop programs for JAMB and WAEC (Windows & Mac) that work fully offline — ideal for homes, schools and CBT centres in areas with poor network.",
                },
              ].map(({ q, a }, i) => (
                <Reveal key={q} delay={i * 60}>
                  <div
                    className="app-feature-card"
                    style={{
                      background: "#FFFFFF",
                      borderRadius: "20px",
                      padding: "24px 24px 22px",
                      height: "100%",
                      position: "relative",
                    }}
                  >
                    <h3
                      style={{
                        fontFamily: "'Baloo 2', sans-serif",
                        fontWeight: 600,
                        fontSize: "16.5px",
                        color: INK,
                        lineHeight: 1.35,
                        marginBottom: "10px",
                      }}
                    >
                      {q}
                    </h3>
                    <p
                      style={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: "13px",
                        color: MUTED,
                        lineHeight: 1.65,
                      }}
                    >
                      {a}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* APP STORE BAND */}
        <section ref={storeRef} style={{ padding: "80px 40px" }}>
          <div
            className="app-store-band"
            style={{
              maxWidth: "1000px",
              margin: "0 auto",
              background: CREAM_CARD,
              borderRadius: "26px",
              padding: "44px 48px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "24px",
              boxShadow: "0 1px 3px rgba(24,22,17,0.06)",
            }}
          >
            <div>
              <h3
                style={{
                  fontFamily: "'Baloo 2', sans-serif",
                  fontWeight: 700,
                  fontSize: "clamp(22px, 2.8vw, 30px)",
                  color: INK,
                  marginBottom: "8px",
                  lineHeight: 1.2,
                }}
              >
                Learn free, right from
                <br />
                your phone.
              </h3>
              <p
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "13.5px",
                  color: MUTED,
                  marginBottom: "20px",
                  maxWidth: "360px",
                }}
              >
                All five Kinteron apps are on Google Play today. iOS versions
                are coming soon.
              </p>
              <StoreLinks
                android="https://play.google.com/store/apps/dev?id=Kinteron"
                size="md"
              />
            </div>

            <div
              className="app-store-visual"
              style={{
                width: "108px",
                height: "108px",
                borderRadius: "28px",
                background: MINT,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                transform: "rotate(-6deg)",
              }}
            >
              <Trophy size={44} color={FOREST} strokeWidth={1.5} />
            </div>
          </div>
        </section>

        {/* FOOTER */}
        <footer style={{ borderTop: `1px solid ${LINE}`, background: CREAM }}>
          <div
            style={{
              maxWidth: "1180px",
              margin: "0 auto",
              padding: "36px 40px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "14px",
            }}
          >
            <span
              style={{
                fontFamily: "'Baloo 2', sans-serif",
                fontWeight: 700,
                fontSize: "18px",
                color: INK,
              }}
            >
              Kinteron
            </span>
            <span
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "12.5px",
                color: MUTED,
                textAlign: "center",
              }}
            >
              Kinteron is not affiliated with JAMB, WAEC, NECO, or any
              government examination body.
            </span>
            <span
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "12.5px",
                color: MUTED,
              }}
            >
              © 2026 Kinteron. Made in Nigeria 🇳🇬
            </span>
          </div>
        </footer>
      </div>

      <UnlockPremiumModal
        open={premiumOpen}
        onClose={() => setPremiumOpen(false)}
      />
      <PaymentSuccessModal
        open={successOpen}
        email={paymentReturnEmail}
        onClose={() => setSuccessOpen(false)}
      />
    </>
  );
}
